import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { PageContainer } from '@/components/shared/PageContainer';
import { queryClient } from '@/lib/utils/queryClient';
import { fallback } from '@/lib/utils/zodFallback';
import { AdminUsersTable } from '../-components/AdminUsersTable';
import { adminUsersQueryOptions } from '../-hooks/useAdminUsers';
import { useAdminUsersPage } from '../-hooks/useAdminUsersPage';

const usersListSearch = z.object({
  page: fallback(z.number().int().min(1), 1),
  limit: fallback(z.number().int().min(10).max(500), 50),
  sort: z
    .string()
    .transform(v => (/^\w+:(ASC|DESC)$/.test(v) ? v : undefined))
    .optional(),
  filters: z
    .union([
      z.string().transform(v => {
        try {
          return JSON.parse(v) as Record<string, string[]>;
        } catch {
          return undefined;
        }
      }),
      z.record(z.string(), z.array(z.string())),
    ])
    .optional(),
});

export const Route = createFileRoute('/management/users/(pages)/')({
  validateSearch: usersListSearch,
  // Off the URL rather than a fixed first page, so a deep link with a sort or
  // filter prefetches the rows it will actually show. `adminUsersQueryOptions`
  // is what makes these agree with the component's own query — it derives the
  // key from the converted params, so both paths hit one cache entry.
  loaderDeps: ({ search }) => ({
    page: search.page ?? 1,
    limit: search.limit ?? 50,
    ...(search.sort !== undefined ? { sort: search.sort } : {}),
    ...(search.filters !== undefined ? { filters: search.filters } : {}),
  }),
  loader: ({ deps }) =>
    queryClient.ensureQueryData(adminUsersQueryOptions(deps)),
  component: ManagementUsersPage,
});

function ManagementUsersPage() {
  const page = useAdminUsersPage();

  return (
    <PageContainer>
      <AdminUsersTable page={page} />
    </PageContainer>
  );
}
